import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { Campaign } from "../models/Campaign";
import {
	CampaignDelayResponse,
	CampaignResponse,
} from "../models/CampaignReponse";
import { HeatMechanicalResult } from "../models/HeatMechanicalResult";
import { HeatTestDisposition } from "../models/HeatTestDisposition";

const moment = getMomentTz();

type GetCampaignsParams = {
	campaignId?: number | null;
	start?: Date | null;
	end?: Date | null;
};

export const getCampaigns = async (
	params?: GetCampaignsParams,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	if (params?.campaignId)
		parameters.push({ name: "CampaignId", value: params.campaignId });
	if (params?.start)
		parameters.push({ name: "Start", value: params.start.toISOString() });
	if (params?.end)
		parameters.push({ name: "End", value: params.end.toISOString() });

	const resp = await apiService.callV2(
		"[M2].[GetCampaigns]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((campaign: CampaignResponse) => ({
			...campaign,
			Start: campaign.Start ? moment.utc(campaign["Start"]).toDate() : null,
			End: campaign.End ? moment.utc(campaign["End"]).toDate() : null,
			SampleStatusTimestamp: campaign.SampleStatusTimestamp
				? moment.utc(campaign["SampleStatusTimestamp"]).toDate()
				: null,
		}));

		return rows as CampaignResponse[];
	} else {
		throw new Error(resp.message || "Error fetching campaign");
	}
};

export const getCampaignDelays = async (
	params: {
		start?: Date;
		end?: Date;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Start", value: params.start?.toISOString() ?? null },
		{ name: "End", value: params.end?.toISOString() ?? null },
	];

	const resp = await apiService.callV2(
		"[M2].[GetCampaignDelays]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as CampaignDelayResponse[];
	} else {
		throw new Error(resp.message || "Error fetching campaign delays");
	}
};

export const upsertCampaign = async (campaign: Campaign) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "CampaignId", value: campaign.CampaignId },
		{ name: "CampaignNumber", value: campaign.CampaignNumber },
		{ name: "SectionId", value: campaign.SectionId },
		{ name: "Start", value: campaign.Start.toISOString() },
		{ name: "End", value: campaign.End?.toISOString() ?? null },
		{ name: "ChargedBillets", value: campaign.ChargedBillets },
		{ name: "ActualColdTons", value: campaign.RolledColdTons },
		{ name: "ActualHotTons", value: campaign.RolledHotTons },
	];

	const resp = await apiService.callV2("[M2].[UpsertCampaign]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error upserting campaign");
	}
};

export const deleteCampaign = async (campaignId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "CampaignId", value: campaignId }];

	const resp = await apiService.callV2("[M2].[DeleteCampaign]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting campaign");
	}
};

export const insertHeatTestDisposition = async ({
	heatId,
	value,
}: {
	heatId: number;
	value: "P" | "R" | "U";
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "HeatId", value: heatId },
		{ name: "SampleStatusCode", value: value },
	];

	const resp = await apiService.callV2(
		"[M2].[InsertHeatTestDispositions]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when upsert sample status");
	}
};

export const getHeatMechanicalResults = async (
	params: { heatId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "HeatId", value: params.heatId }];

	const resp = await apiService.callV2(
		"[M2].[GetHeatMechanicalResults]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as HeatMechanicalResult[];
	} else {
		throw new Error(resp.message || "Error fetching heat mechanical results");
	}
};

export const getHeatTestDispositions = async (
	params: { heatId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "HeatId", value: params.heatId }];

	const resp = await apiService.callV2(
		"[M2].[GetHeatTestDispositions]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((row: HeatTestDisposition) => ({
			...row,
			Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
		}));

		return rows as HeatTestDisposition[];
	} else {
		throw new Error(resp.message || "Error fetching heat sample status");
	}
};
